// FE-SCRM-051 — REST client for the Add Opportunity affordance.
//
// Three endpoints touched:
//   * POST  /api/v1/sales/opportunities                — create.
//   * GET   /api/v1/sales/broker-firms/suggestions     — autocomplete.
//   * GET   /api/v1/sales/broker-offices/suggestions   — autocomplete.
// The team-members + broker-locations option lists are sourced from
// ``features/settings/api`` so the membership/location vocabulary
// stays in one place across the SPA.

import { api } from "@/api/client";

import type { CreateOpportunityInput, CreatedOpportunity } from "./types";


export function createOpportunity(
  input: CreateOpportunityInput,
): Promise<CreatedOpportunity> {
  // Strip nulls/undefineds for cleanliness; the BE accepts them but
  // the on-wire payload is easier to read when only set fields
  // travel. ``prospect_id`` + ``opportunity_type`` + renewal cols
  // are always present (required).
  const body: Record<string, unknown> = {
    prospect_id: input.prospect_id,
    opportunity_type: input.opportunity_type,
    renewal_month: input.renewal_month,
    renewal_year: input.renewal_year,
  };
  const optional: (keyof CreateOpportunityInput)[] = [
    "name", "estimated_lives", "estimated_annual_revenue_cents",
    "estimated_commission_cents", "probability", "expected_close_date",
    "producer_id", "owner_id", "broker_location_id",
    "incumbent_broker_firm", "incumbent_broker_office", "next_step",
    "bor_target_date", "bor_effective_date",
  ];
  for (const key of optional) {
    const value = input[key];
    if (value !== undefined && value !== null && value !== "") {
      body[key] = value;
    }
  }
  return api.post<CreatedOpportunity>(
    "/api/v1/sales/opportunities",
    body,
  );
}


export interface BrokerFirmSuggestion {
  name: string;
}


export interface BrokerFirmSuggestionsResponse {
  rows: BrokerFirmSuggestion[];
}


export function fetchBrokerFirmsSuggestions(
  q: string,
  opts?: { signal?: AbortSignal },
): Promise<BrokerFirmSuggestionsResponse> {
  const params = new URLSearchParams();
  if (q) params.set("q", q);
  const qs = params.toString();
  return api.get<BrokerFirmSuggestionsResponse>(
    `/api/v1/sales/broker-firms/suggestions${qs ? `?${qs}` : ""}`,
    opts,
  );
}


export function fetchBrokerOfficesSuggestions(
  q: string,
  firm: string | null,
  opts?: { signal?: AbortSignal },
): Promise<BrokerFirmSuggestionsResponse> {
  const params = new URLSearchParams();
  if (q) params.set("q", q);
  if (firm) params.set("firm", firm);
  const qs = params.toString();
  return api.get<BrokerFirmSuggestionsResponse>(
    `/api/v1/sales/broker-offices/suggestions${qs ? `?${qs}` : ""}`,
    opts,
  );
}
